/**
 * Created by katarinababic on 11.6.25.
 */
import * as React from 'react';
import { observer } from 'mobx-react';
import { Theme } from '../../Theme/Theme';
import styled from '@emotion/styled';

const Container = styled.div<{ disabled?: boolean; onPress?: () => void }>`
  cursor: ${(props) => (props.disabled ? 'not-allowed' : props.onPress ? 'pointer' : 'default')};
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};
  background: ${Theme.Colors.white};
  border-radius: 14px;
  padding: 12px;
  display: flex;
  align-items: center;
  min-height: 80px;
  border: 1px solid ${Theme.Colors.lighterGrey};
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
  transition: all 0.15s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    border-color: ${Theme.Colors.primary}30;
  }

  &:active {
    transform: scale(0.995);
  }
`;

const Thumbnail = styled.div<{ imageSource: string }>`
  width: 56px;
  height: 56px;
  background-image: ${(props) => props.imageSource};
  background-size: cover;
  background-position: center;
  border-radius: 10px;
  flex-shrink: 0;
`;

const Content = styled.div<{ hasImage: boolean }>`
  flex: 1;
  margin-left: ${(props) => (props.hasImage ? '12px' : '0px')};
  margin-right: 12px;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 2px 0;
`;

const TitleText = styled.div`
  font-weight: 600;
  font-size: 15px;
  line-height: 20px;
  color: ${Theme.Colors.textColor};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SubtitleText = styled.div`
  font-size: 13px;
  line-height: 17px;
  color: ${Theme.Colors.grey};
  margin-top: 3px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const DescriptionText = styled.div`
  font-size: 13px;
  color: ${Theme.Colors.grey};
  margin-top: 4px;
`;

const RightColumn = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
`;

const StatusIndicator = styled.div<{ done?: boolean }>`
  width: 24px;
  height: 24px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(props) => (props.done ? Theme.Colors.primary : 'transparent')};
  border: ${(props) => (props.done ? 'none' : `2px solid ${Theme.Colors.lighterGrey}`)};
  transition: all 0.2s ease;

  & > i {
    color: ${Theme.Colors.white};
    font-size: 14px;
  }
`;

export type WorkoutDayCardEventListItemProps = {
  onPress?: () => void;
  onLongPress?: () => void;
  disabled?: boolean;
  title?: string;
  subtitle?: React.ReactNode;
  description?: React.ReactNode;
  showAsDone?: boolean;
  RightColumnComponent?: React.ReactNode;
  imageSource?: React.CSSProperties['backgroundImage'];
};

export const WorkoutDayCardEventListItem: React.FC<WorkoutDayCardEventListItemProps> = observer((props) => {
  const {
    onPress,
    onLongPress,
    disabled,
    title,
    subtitle,
    description,
    showAsDone,
    RightColumnComponent,
    imageSource,
  } = props;

  const handleClick = (e: React.MouseEvent) => {
    if (!disabled && onPress) {
      onPress();
    }
  };

  const handleContextMenu = (e: React.MouseEvent) => {
    if (!disabled && onLongPress) {
      e.preventDefault();
      onLongPress();
    }
  };

  return (
    <Container onClick={handleClick} onContextMenu={handleContextMenu} disabled={disabled} onPress={onPress}>
      {imageSource && <Thumbnail imageSource={imageSource} />}
      <Content hasImage={!!imageSource}>
        {title && <TitleText>{title}</TitleText>}
        {subtitle && <SubtitleText>{subtitle}</SubtitleText>}
        {description && <DescriptionText>{description}</DescriptionText>}
      </Content>
      <RightColumn>
        {RightColumnComponent}
        <StatusIndicator done={showAsDone}>
          {showAsDone && <i className="mdi mdi-check" />}
        </StatusIndicator>
      </RightColumn>
    </Container>
  );
});
